import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'
import type { GetState } from 'types/redux'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { receiveImportantUpdateNotification } from '@disco/state-management/actions/NotificationActions'
import { updateSearchParams } from '@disco/state-management/actions/SearchActions'
import api from '@disco/api/server'
import Storage from 'disco-storage'

import {
  APP_INIT_SUCCESS,
  APP_ONLINE_STATUS,
  APP_SET_IS_ONBOARDING,
  APP_SEARCH_PAGE_SHORTLIST_TOGGLE,
  APP_SKIP_ONBOARDING,
  APP_PAGE_CHANGE_LOAD,
  APP_SWITCH_OFF_DESIGN_BANNER,
  DATA_LOAD,
  FOCUS_ON_EXPLORER,
  SHORTLISTS_TOGGLE,
  SIDEBAR_CLOSE,
  SIDEBAR_OPEN,
  WINDOW_RESIZE,
  APP_PLAYLIST_CREATOR_SETTINGS_UPDATED,
  APP_PLAYLIST_CREATOR_WIDTH_UPDATED,
} from '@disco/constants/ActionTypes'
import { PHONE } from '@disco/constants/DeviceTypes'
import type {
  State as AppState,
  PlaylistCreatorUserSettings,
} from '@disco/state-management/reducers/app'
import { getContactsList } from '@disco/state-management/selectors/contacts'
import mixpanel from '@disco/api/mixpanel'
import {
  setPlaylistCreatorUserSettings,
  getPlaylistCreatorSettingsMixpanelProperties,
} from '@disco/utils/shortlist'
import type {
  FormattedPlaylistToggleSettings,
  PlaylistCreatorActionType,
  PlaylistCreatorLocation,
  PlaylistCreatorVisibility,
} from '@disco/utils/shortlist'
/**
 * Toggles the sidebar open/closed state and handles mobile-specific behavior.
 * On mobile devices, also closes the search panel when toggling sidebar.
 * @returns Thunk function that dispatches sidebar toggle action
 */
export function toggleSidebar(): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState): void => {
    const { sidebarOpen, mobileDeviceType }: AppState = getState().app
    dispatch({
      type: sidebarOpen ? SIDEBAR_CLOSE : SIDEBAR_OPEN,
    })

    if (mobileDeviceType === PHONE) {
      dispatch(
        updateSearchParams({
          searchOpen: false,
          searchHeight: null,
          searchCompact: false,
        }),
      )
    }
  }
}
/**
 * Indicates successful app initialization.
 * @returns Action object with APP_INIT_SUCCESS type
 */
export const appInitSuccess = (): {
  type: string
} => ({
  type: APP_INIT_SUCCESS,
})
/**
 * Opens the sidebar.
 * @returns Action object with SIDEBAR_OPEN type
 */
export const openSidebar = (): {
  type: string
} => ({
  type: SIDEBAR_OPEN,
})

/**
 * Controls the shortlists panel visibility and handles mobile navigation.
 * @param forceState - Optional boolean to force open (true) or closed (false). If not provided, toggles current state.
 * @param isSearchPage - Optional boolean to indicate if the current page is the search page.
 * @returns Thunk function that dispatches navigation and state change actions
 */

const STORAGE_KEYS = {
  Explorer: 'playlist_creator_open',
  Search: 'search_page_playlist_creator_open',
}

const setPlaylistStorageAndMixpanelEvent = (
  toggleSettings: FormattedPlaylistToggleSettings,
  newState: boolean,
  isUpdated?: boolean,
) => {
  const location = toggleSettings?.curr?.location
  if (location) {
    mixpanel.sendPureEvent(
      'Change Playlist Creator Visibility',
      getPlaylistCreatorSettingsMixpanelProperties(toggleSettings) ?? undefined,
    )
    if (!isUpdated) return
    Storage.set(STORAGE_KEYS[location], newState)
  }
}

const toVisibility = (isOpen: boolean): PlaylistCreatorVisibility =>
  isOpen ? 'Visible' : 'Hidden'

function getToggleSettings(
  currentLocation: PlaylistCreatorLocation = 'Explorer',
  actionType: PlaylistCreatorActionType = 'Load Page',
  isVisible?: boolean,
): (dispatch: any, getState: GetState) => any {
  return (dispatch: any, getState: GetState) => {
    const { shortlistUserSettings }: AppState = getState().app

    const location = currentLocation.toLowerCase()

    const currentSettings = {
      location: currentLocation,
      settings: {
        [location]: {
          visible: toVisibility(Boolean(isVisible)),
          actionType,
        },
      },
    }

    return setPlaylistCreatorUserSettings(
      shortlistUserSettings,
      currentSettings,
    )
  }
}

export function updatePlaylistCreatorSettings(
  newState: boolean,
  isUpdated: boolean,
  toggleSettings: FormattedPlaylistToggleSettings,
): (dispatch: any) => void {
  return dispatch => {
    if (!toggleSettings?.curr) return

    dispatch({
      type: APP_PLAYLIST_CREATOR_SETTINGS_UPDATED,
      shortlistUserSettings: toggleSettings.curr,
    })

    setPlaylistStorageAndMixpanelEvent(toggleSettings, newState, isUpdated)
  }
}

// Update App settings on page/path change when necessary
export function updatePageChangeSettings(
  currentLocation: PlaylistCreatorLocation,
  settings: Record<string, any>,
): (dispatch: any) => void {
  return (dispatch: (...args: Array<any>) => any): void => {
    if (!currentLocation) return
    dispatch({
      type: APP_PAGE_CHANGE_LOAD,
      updateSettings: {
        currentLocation,
        ...settings,
      },
    })
  }
}

export function toggleShortlists(
  forceState?: boolean,
  currentLocation?: PlaylistCreatorLocation,
  actionType?: PlaylistCreatorActionType,
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState): void => {
    const {
      shortlistsOpen,
      isSearchPageShortlistOpen,
      shortlistUserSettings,
    }: AppState = getState().app
    const { location: previousLocation } = shortlistUserSettings

    const isSearchPage = currentLocation === 'Search'

    const isPlaylistCreatorOpen = isSearchPage
      ? isSearchPageShortlistOpen
      : shortlistsOpen

    const newState =
      forceState !== undefined ? forceState : !isPlaylistCreatorOpen

    const isStateChanged = !isEqual(isPlaylistCreatorOpen, newState)
    const toggleSettings = dispatch(
      getToggleSettings(currentLocation, actionType, newState),
    )

    if (currentLocation && actionType && toggleSettings?.curr) {
      dispatch(
        updatePlaylistCreatorSettings(newState, isStateChanged, toggleSettings),
      )
    }

    if (!isStateChanged) return

    const toggleDispatchType = isSearchPage
      ? APP_SEARCH_PAGE_SHORTLIST_TOGGLE
      : SHORTLISTS_TOGGLE

    dispatch({
      type: toggleDispatchType,
    })
  }
}

/**
 * Focuses on the explorer component.
 * @returns Action object with FOCUS_ON_EXPLORER type
 */
export const focusOnExplorer = (): {
  type: string
} => ({
  type: FOCUS_ON_EXPLORER,
})
/**
 * Hides the design banner.
 * @returns Action object with APP_SWITCH_OFF_DESIGN_BANNER type
 */
export const switchOffDesignBanner = (): {
  type: string
} => ({
  type: APP_SWITCH_OFF_DESIGN_BANNER,
})
/**
 * Handles window resize events by updating the stored window width if it has changed.
 * @returns Thunk function that dispatches window resize action if width changed
 */
export function handleResize(): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState): void => {
    const { windowWidth }: AppState = getState().app

    if (window.innerWidth !== windowWidth) {
      dispatch({
        type: WINDOW_RESIZE,
        windowWidth: window.innerWidth,
      })
    }
  }
}
const launchTimestamp = new Date()
/**
 * Checks for application updates and shows notification if a newer version is available.
 * Compares server update timestamp with app launch time to determine if update is needed.
 * @returns Thunk function that checks for updates and dispatches notification if needed
 */
export const checkUpdate = (): ((dispatch: any) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    try {
      const updateResult = await api.getDiscoUpdate()
      if (!updateResult) return
      const update = new Date(updateResult.update * 1000)

      if (updateResult.target === 'v1') {
        if (FRONTEND_VERSION === 'v2') return
      }

      if (update > launchTimestamp) {
        dispatch(receiveImportantUpdateNotification())
      }
    } catch (error) {
      if (error.status !== 404) sendGenericError(error)
    }
  }
}
/**
 * Updates the app's online/offline status.
 * @param online - Boolean indicating if the app is online
 * @returns Action object with APP_ONLINE_STATUS type and online status
 */
export const changeOnlineStatus = (
  online: boolean,
): {
  online: boolean
  type: string
} => ({
  type: APP_ONLINE_STATUS,
  online,
})
/**
 * Sets the onboarding state for the application.
 * @param isOnboarding - Boolean indicating if user is in onboarding flow
 * @returns Thunk function that dispatches onboarding state change
 */
export const setIsOnboarding = (
  isOnboarding: boolean,
): ((dispatch: any) => void) => {
  return (dispatch: (...args: Array<any>) => any): void => {
    dispatch({
      type: APP_SET_IS_ONBOARDING,
      isOnboarding,
    })
  }
}

export const setPlaylistCreatorWidth = (
  width: number,
): ((dispatch: any) => void) => {
  return (dispatch: (...args: Array<any>) => any): void => {
    dispatch({
      type: APP_PLAYLIST_CREATOR_WIDTH_UPDATED,
      playlistCreatorWidth: width,
    })
  }
}
/**
 * Skips the onboarding flow for the current user.
 * @returns Thunk function that dispatches skip onboarding action
 */
export const skipOnboarding = (): ((dispatch: any) => void) => {
  return (dispatch: (...args: Array<any>) => any): void => {
    dispatch({
      type: APP_SKIP_ONBOARDING,
    })
  }
}
/**
 * Requests required data from the server, optimizing the request by filtering out already loaded contacts.
 * @param request - Object containing arrays of data to request (e.g., contacts)
 * @returns Thunk function that fetches optimized data and dispatches DATA_LOAD action
 */
export const requireData = (request: {
  contacts: string[]
}): ((dispatch: any, getState: GetState) => Promise<any>) => {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    try {
      let optimizedRequest = request

      if (request.contacts) {
        const contacts = getContactsList(getState()) ?? []
        optimizedRequest.contacts = request.contacts.filter(
          id =>
            !contacts.find(
              existingContact => existingContact.id === Number(id),
            ),
        )

        if (optimizedRequest.contacts.length === 0) {
          // @ts-expect-error todo fix later
          optimizedRequest = omit(optimizedRequest, 'contacts')
        }
      }

      if (Object.keys(optimizedRequest).length > 0) {
        const data = await api.requireData(optimizedRequest)
        return dispatch({
          type: DATA_LOAD,
          data,
        })
      } else return Promise.resolve(null)
    } catch (e) {
      dispatch(sendGenericError(e))
    }
  }
}
